import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StudentLearningContentRenderer from "./StudentLearningContentRenderer";
import {
  completeStudentLearningContent,
  fetchStudentLearningContent,
  fetchStudentLearningContentMedia,
  normalizeStudentLearningContentAccess,
} from "./utils/studentLearningContentApi";
import "./StudentLearningContent.css";

export const learningContentAccessFromLocation = (location) => {
  const stateAccess = location?.state?.learningContentAccess;
  if (stateAccess) return normalizeStudentLearningContentAccess(stateAccess);
  const assignmentId = new URLSearchParams(location?.search || "").get("assignmentId")?.trim();
  if (assignmentId) {
    return normalizeStudentLearningContentAccess({ type: "assignment", assignmentId });
  }
  return null;
};

const StudentLearningContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [retryKey, setRetryKey] = useState(0);
  const [status, setStatus] = useState("loading");
  const [content, setContent] = useState(null);
  const [mediaUrls, setMediaUrls] = useState({});
  const [error, setError] = useState("");
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionError, setCompletionError] = useState("");
  const mediaUrlsRef = useRef({});
  const contentCardRef = useRef(null);
  const bottomSentinelRef = useRef(null);

  const accessResolution = useMemo(() => {
    try {
      return { access: learningContentAccessFromLocation(location), error: "" };
    } catch (_error) {
      return { access: null, error: "This Learning Content link is invalid." };
    }
  }, [location]);
  const access = accessResolution.access;
  const safeReturnPath = access?.type === "assignment" ? "/my-assessments" : "/enter-quiz";

  useEffect(() => {
    const revoke = (urls) => {
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
    revoke(mediaUrlsRef.current);
    mediaUrlsRef.current = {};
    setMediaUrls({});
    setContent(null);
    setHasReachedBottom(false);
    setIsCompleting(false);
    setCompletionError("");

    if (!access) {
      setStatus("invalid");
      setError(accessResolution.error || "Open Learning Content from a valid assessment link.");
      return undefined;
    }

    let active = true;
    let failed = false;
    const controller = new AbortController();
    const createdUrls = {};
    setStatus("loading");
    setError("");

    const load = async () => {
      try {
        const response = await fetchStudentLearningContent(access, {
          signal: controller.signal,
        });
        const mediaBlocks = response.learningContent.blocks.filter(
          (block) => block.type === "image" || block.type === "resource"
        );
        await Promise.all(
          mediaBlocks.map(async (block) => {
            const blob = await fetchStudentLearningContentMedia(
              access,
              block.id,
              response.learningContent.revision,
              { signal: controller.signal }
            );
            if (!active || failed || controller.signal.aborted) return;
            createdUrls[block.id] = URL.createObjectURL(blob);
          })
        );
        if (!active) {
          revoke(createdUrls);
          return;
        }
        mediaUrlsRef.current = createdUrls;
        setMediaUrls(createdUrls);
        setContent(response);
        setStatus("ready");
      } catch (loadError) {
        failed = true;
        controller.abort();
        revoke(createdUrls);
        if (!active || loadError?.name === "AbortError") return;
        if (loadError?.code === "assignment-closed") {
          setHasReachedBottom(false);
          setError("The submission deadline has passed");
          setStatus("closed");
          return;
        }
        setError(
          loadError?.message ||
            "Learning Content could not be loaded safely. Please try again."
        );
        setStatus("error");
      }
    };

    load();
    return () => {
      active = false;
      controller.abort();
      revoke(createdUrls);
      if (mediaUrlsRef.current === createdUrls) mediaUrlsRef.current = {};
    };
  }, [access, accessResolution.error, retryKey]);

  useEffect(() => {
    if (status !== "ready" || !access) return undefined;
    let active = true;
    const checkDeadline = async () => {
      try {
        await fetchStudentLearningContent(access);
      } catch (checkError) {
        if (!active || checkError?.code !== "assignment-closed") return;
        setHasReachedBottom(false);
        setCompletionError("The submission deadline has passed");
        setError("The submission deadline has passed");
        setStatus("closed");
      }
    };
    const interval = window.setInterval(checkDeadline, 5000);
    const onVisibility = () => { if (document.visibilityState === "visible") checkDeadline(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [access, status]);

  useEffect(() => {
    if (status !== "ready" || !content || !bottomSentinelRef.current) return undefined;

    let active = true;
    let observer = null;
    const imageCleanups = [];
    const sentinel = bottomSentinelRef.current;

    const markIfVisibleWithoutObserver = () => {
      if (!active) return;
      const bounds = sentinel.getBoundingClientRect();
      if (bounds.top <= window.innerHeight && bounds.bottom >= 0) {
        setHasReachedBottom(true);
      }
    };

    const observeSentinel = () => {
      if (!active) return;
      if (typeof IntersectionObserver !== "function") {
        markIfVisibleWithoutObserver();
        window.addEventListener("scroll", markIfVisibleWithoutObserver, { passive: true });
        window.addEventListener("resize", markIfVisibleWithoutObserver);
        return;
      }
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasReachedBottom(true);
        }
      }, { root: null, threshold: 1 });
      observer.observe(sentinel);
    };

    const images = Array.from(contentCardRef.current?.querySelectorAll("img") || []);
    let unsettledImages = images.filter((image) => !image.complete).length;
    if (unsettledImages === 0) {
      observeSentinel();
    } else {
      const imageSettled = () => {
        unsettledImages -= 1;
        if (unsettledImages === 0) observeSentinel();
      };
      images.filter((image) => !image.complete).forEach((image) => {
        image.addEventListener("load", imageSettled, { once: true });
        image.addEventListener("error", imageSettled, { once: true });
        imageCleanups.push(() => {
          image.removeEventListener("load", imageSettled);
          image.removeEventListener("error", imageSettled);
        });
      });
    }

    return () => {
      active = false;
      observer?.disconnect();
      imageCleanups.forEach((cleanup) => cleanup());
      window.removeEventListener("scroll", markIfVisibleWithoutObserver);
      window.removeEventListener("resize", markIfVisibleWithoutObserver);
    };
  }, [content, status]);

  useEffect(
    () => () => {
      Object.values(mediaUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      mediaUrlsRef.current = {};
    },
    []
  );

  const navigateToAssessment = (includeLearningContentHandoff) => {
    const state = includeLearningContentHandoff
      ? {
          learningContentAccess: access,
          learningContentRevision: content.learningContent.revision,
        }
      : undefined;
    if (access.type === "assignment") {
      const destination = `/enter-quiz?assignmentId=${encodeURIComponent(access.assignmentId)}`;
      navigate(destination, {
        replace: true,
        ...(state ? { state } : {}),
      });
      return;
    }
    const destination =
      `/enter-quiz?teacherUid=${encodeURIComponent(access.teacherUid)}` +
      `&quizCode=${encodeURIComponent(access.quizCode)}`;
    navigate(destination, {
      replace: true,
      ...(state ? { state } : {}),
    });
  };

  const continueToAssessment = async () => {
    if (
      status !== "ready" ||
      !content ||
      !hasReachedBottom ||
      isCompleting
    ) return;
    setIsCompleting(true);
    setCompletionError("");
    try {
      const result = await completeStudentLearningContent(
        access,
        content.learningContent.revision,
        content.learningContent.learningContentVersion
      );
      if (result.status === "not-required") {
        navigateToAssessment(false);
        return;
      }
      navigateToAssessment(true);
    } catch (completionFailure) {
      if (completionFailure?.code === "learning-content-changed") {
        setHasReachedBottom(false);
        setRetryKey((value) => value + 1);
        return;
      }
      if (completionFailure?.code === "assignment-closed") {
        setHasReachedBottom(false);
        setError("The submission deadline has passed");
        setStatus("closed");
        return;
      }
      if (completionFailure?.status === 401 || completionFailure?.status === 403) {
        setContent(null);
        setError(
          completionFailure?.message ||
            "Your access to this Learning Content could not be confirmed."
        );
        setStatus("error");
        return;
      }
      setCompletionError(
        completionFailure?.message ||
          "Your progress could not be saved. Please try again before starting the assessment."
      );
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <main className="slc-page" aria-busy={status === "loading" ? "true" : "false"}>
      <div className="slc-shell">
        {status === "loading" && (
          <section className="slc-state-card slc-loading-card" role="status" aria-live="polite">
            <div className="slc-loading-indicator" aria-hidden="true">
              <span className="slc-loading-ring" />
              <span className="slc-loading-core" />
            </div>
            <p className="slc-kicker">Learning Content</p>
            <h1>Preparing your learning material</h1>
            <p>We’re securely loading everything your teacher has shared for this assessment.</p>
          </section>
        )}

        {(status === "error" || status === "invalid") && (
          <section className="slc-state-card slc-error-card" role="alert">
            <div className="slc-error-icon" aria-hidden="true">!</div>
            <p className="slc-kicker">Learning Content unavailable</p>
            <h1>We couldn’t open this material safely</h1>
            <p>{error}</p>
            <p className="slc-error-reassurance">
              Your assessment has not started and no attempt has been used.
            </p>
            <div className="slc-state-actions">
              {status === "error" && (
                <button type="button" className="slc-primary-button" onClick={() => setRetryKey((value) => value + 1)}>
                  Retry
                </button>
              )}
              <button type="button" className="slc-secondary-button" onClick={() => navigate(safeReturnPath)}>
                {access?.type === "assignment" ? "Back to My Assessments" : "Back to Test Entry"}
              </button>
            </div>
          </section>
        )}

        {status === "closed" && (
          <section className="slc-state-card slc-error-card" role="alert">
            <p className="slc-kicker">Learning Content</p>
            <h1>Closed</h1>
            <p>The submission deadline has passed</p>
            <button type="button" className="slc-secondary-button" onClick={() => navigate(safeReturnPath)}>
              {access?.type === "assignment" ? "Back to My Assessments" : "Back to Test Entry"}
            </button>
          </section>
        )}

        {status === "ready" && content && (
          <>
            <header className="slc-hero">
              <button type="button" className="slc-back-button" onClick={() => navigate(safeReturnPath)}>
                ← {access.type === "assignment" ? "My Assessments" : "Test Entry"}
              </button>
              <p className="slc-kicker">Learning Content</p>
              <h1>{content.quiz.title}</h1>
              <p>Review the following material before beginning your assessment.</p>
            </header>

            <article className="slc-content-card" ref={contentCardRef}>
              <StudentLearningContentRenderer
                blocks={content.learningContent.blocks}
                mediaUrls={mediaUrls}
              />
            </article>

            <div
              ref={bottomSentinelRef}
              className="slc-bottom-sentinel"
              aria-hidden="true"
            />

            <footer className="slc-action-card" aria-busy={isCompleting ? "true" : "false"}>
              <div>
                <p className="slc-action-eyebrow">Ready when you are</p>
                <h2>Continue to your assessment</h2>
                <p>Your timer has not started while reviewing this page.</p>
                {!hasReachedBottom && (
                  <p className="slc-gate-help" id="slc-gate-help">
                    Review all the material to enable Continue.
                  </p>
                )}
                {completionError && (
                  <p className="slc-completion-error" role="alert">
                    {completionError}
                  </p>
                )}
              </div>
              <button
                type="button"
                className={`slc-primary-button${isCompleting ? " is-saving" : ""}`}
                onClick={continueToAssessment}
                disabled={!hasReachedBottom || isCompleting}
                aria-describedby={!hasReachedBottom ? "slc-gate-help" : undefined}
              >
                {isCompleting ? "Saving progress\u2026" : "Continue to Assessment"}
              </button>
            </footer>
          </>
        )}
      </div>
    </main>
  );
};

export default StudentLearningContent;
